import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

/**
 * Hook personalizado para detectar el tamaño de pantalla actual
 * Basado en los breakpoints de Material-UI
 * 
 * @returns {Object} Objeto con información sobre el tamaño de pantalla
 * @property {boolean} isMobile - true si está en mobile (xs o sm)
 * @property {boolean} isTablet - true si está en tablet (md)
 * @property {boolean} isDesktop - true si está en desktop (lg o xl)
 * @property {boolean} isXs - true si está en extra small (< 600px)
 * @property {boolean} isSm - true si está en small (600px - 900px)
 * @property {boolean} isMd - true si está en medium (900px - 1200px)
 * @property {boolean} isLg - true si está en large (1200px - 1536px)
 * @property {boolean} isXl - true si está en extra large (>= 1536px)
 * @property {string} breakpoint - Nombre del breakpoint actual ('xs', 'sm', 'md', 'lg', 'xl')
 * @property {boolean} isMobileOrTablet - true si está en mobile o tablet
 * @property {boolean} isTabletOrDesktop - true si está en tablet o desktop
 */
export const useResponsive = () => {
  const theme = useTheme();

  // Detectar cada breakpoint
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));
  const isSm = useMediaQuery(theme.breakpoints.only("sm"));
  const isMd = useMediaQuery(theme.breakpoints.only("md"));
  const isLg = useMediaQuery(theme.breakpoints.only("lg"));
  const isXl = useMediaQuery(theme.breakpoints.only("xl"));

  // Detectar rangos de breakpoints (más útiles)
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // xs y sm
  const isTablet = useMediaQuery(theme.breakpoints.only("md")); // md
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg")); // lg y xl

  // Combinaciones útiles
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md")); // xs, sm, md
  const isTabletOrDesktop = useMediaQuery(theme.breakpoints.up("md")); // md, lg, xl

  // Determinar el breakpoint actual
  let breakpoint = "xs";
  if (isXl) breakpoint = "xl";
  else if (isLg) breakpoint = "lg";
  else if (isMd) breakpoint = "md";
  else if (isSm) breakpoint = "sm";

  return {
    // Categorías principales
    isMobile,
    isTablet,
    isDesktop,

    // Breakpoints individuales
    isXs,
    isSm,
    isMd,
    isLg,
    isXl,

    // Breakpoint actual
    breakpoint,

    // Combinaciones útiles
    isMobileOrTablet,
    isTabletOrDesktop,
  };
};

export default useResponsive;
